import { DeleteOutlined, EyeOutlined, FilterOutlined } from '@ant-design/icons';
import { Button, Dropdown, Flex, Input, Modal, Space, Table, Select, Typography, Tag, Drawer } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import contactApi from '../../api/contact';
import SH_Toolbar from '../../components/SH_Toolbar';

const { Title, Text } = Typography;

const statusMeta = {
	new: { color: 'red', label: 'Mới' },
	read: { color: 'blue', label: 'Đã đọc' },
	replied: { color: 'green', label: 'Đã phản hồi' },
	closed: { color: 'default', label: 'Đã đóng' },
};

const SH_contact = () => {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [total, setTotal] = useState(0);

	const [filter, setFilter] = useState({
		page: 1,
		limit: 10,
		search: '',
		status: undefined,
	});

	const [open, setOpen] = useState(false);
	const [detail, setDetail] = useState(null);

	const [draft, setDraft] = useState({ status: undefined, note: '' });
	const [dirty, setDirty] = useState(false);

	const fetchList = (_filter = filter) => {
		setLoading(true);
		contactApi
			.get({ ..._filter })
			.then((res) => {
				setItems(res?.data?.data?.items || []);
				setTotal(res?.data?.data?.pagination?.total || 0);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		fetchList(filter);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const applyFilter = (patch) => {
		const next = { ...filter, ...patch, page: 1 };
		setFilter(next);
		fetchList(next);
	};

	const handlePageChange = (page, pageSize) => {
		const next = { ...filter, page, limit: pageSize };
		setFilter(next);
		fetchList(next);
	};

	const openDetail = async (record) => {
		setLoading(true);
		try {
			const res = await contactApi.getById(record._id);
			const data = res?.data?.data || null;

			setDetail(data);
			setDraft({ status: data?.status, note: data?.note || '' });
			setDirty(false);
			setOpen(true);

			if (record.status === 'new') {
				await contactApi.update(record._id, { status: 'read' });
				setDetail((prev) => (prev ? { ...prev, status: 'read' } : prev));
				setDraft((prev) => ({ ...prev, status: 'read' }));
				fetchList(filter);
			}
		} finally {
			setLoading(false);
		}
	};

	const confirmDelete = (record) => {
		Modal.confirm({
			title: 'Xác nhận xóa',
			content: 'Bạn có chắc chắn muốn xóa liên hệ này không?',
			okText: 'Xóa',
			cancelText: 'Hủy',
			okButtonProps: { danger: true },
			onOk: async () => {
				setLoading(true);
				try {
					await contactApi.delete(record._id);
					fetchList(filter);
				} finally {
					setLoading(false);
				}
			},
		});
	};

	const handleCloseDrawer = () => {
		if (!dirty) {
			setOpen(false);
			return;
		}
		Modal.confirm({
			title: 'Chưa lưu thay đổi',
			content: 'Bạn có muốn bỏ thay đổi không?',
			okText: 'Bỏ thay đổi',
			cancelText: 'Ở lại',
			okButtonProps: { danger: true },
			onOk: () => {
				setOpen(false);
				setDirty(false);
			},
		});
	};

	const handleResetDraft = () => {
		if (!detail) return;
		setDraft({ status: detail.status, note: detail.note || '' });
		setDirty(false);
	};

	const handleSaveDraft = async () => {
		if (!detail) return;
		setLoading(true);
		try {
			const payload = {
				status: draft.status,
				note: draft.note || '',
			};
			const res = await contactApi.update(detail._id, payload);

			const newDetail = res?.data?.data || { ...detail, ...payload };
			setDetail(newDetail);
			setDraft({ status: newDetail.status, note: newDetail.note || '' });
			setDirty(false);

			fetchList(filter);
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			title: 'Họ tên',
			dataIndex: 'fullName',
			key: 'fullName',
			ellipsis: true,
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
			ellipsis: true,
		},
		{
			title: 'Chủ đề',
			dataIndex: 'subject',
			key: 'subject',
			ellipsis: true,
		},
		{
			title: 'Trạng thái',
			dataIndex: 'status',
			key: 'status',
			width: 140,
			render: (s) => {
				const meta = statusMeta[s] || statusMeta.new;
				return <Tag color={meta.color}>{meta.label}</Tag>;
			},
		},
		{
			title: 'Thời gian',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 190,
			render: (v) => (v ? dayjs(v).format('HH:mm DD/MM/YYYY') : ''),
		},
		{
			title: 'Thao tác',
			key: 'action',
			width: 200,
			render: (_, record) => (
				<Space>
					<Button icon={<EyeOutlined />} onClick={() => openDetail(record)}>
						Xem
					</Button>
					<Button danger type="dashed" icon={<DeleteOutlined />} onClick={() => confirmDelete(record)}>
						Xóa
					</Button>
				</Space>
			),
		},
	];

	return (
		<>
			<Flex align="center" justify="space-between" wrap="wrap" gap={12} style={{ marginBottom: 12 }}>
				<Title level={2} style={{ margin: 0 }}>
					Liên hệ
				</Title>
			</Flex>

			<SH_Toolbar
				left={() => null}
				right={() => (
					<Flex gap={8} wrap="wrap" align="center">
						<Input.Search
							allowClear
							style={{ width: 280 }}
							placeholder="Tìm theo tên / email / chủ đề"
							value={filter.search}
							onChange={(e) => applyFilter({ search: e.target.value })}
						/>

						<Dropdown
							trigger={['click']}
							placement="bottomRight"
							dropdownRender={() => (
								<div style={{ width: 320, background: '#fff', padding: 12, borderRadius: 12 }}>
									<Space direction="vertical" size={12} style={{ width: '100%' }}>
										<div>
											<div style={{ fontWeight: 600, marginBottom: 6 }}>Trạng thái</div>
											<Select
												allowClear
												style={{ width: '100%' }}
												value={filter.status}
												placeholder="Tất cả"
												options={[
													{ label: 'Mới', value: 'new' },
													{ label: 'Đã đọc', value: 'read' },
													{ label: 'Đã phản hồi', value: 'replied' },
													{ label: 'Đã đóng', value: 'closed' },
												]}
												onChange={(v) => applyFilter({ status: v })}
											/>
										</div>

										<Flex justify="flex-end" gap={8}>
											<Button
												onClick={() => {
													const next = {
														page: 1,
														limit: filter.limit,
														search: '',
														status: undefined,
													};
													setFilter(next);
													fetchList(next);
												}}
											>
												Reset
											</Button>
											<Button type="primary" onClick={() => fetchList(filter)}>
												Áp dụng
											</Button>
										</Flex>
									</Space>
								</div>
							)}
						>
							<Button icon={<FilterOutlined />}>Lọc</Button>
						</Dropdown>
					</Flex>
				)}
			/>

			<Table
				rowKey={(r) => r._id}
				loading={loading}
				columns={columns}
				dataSource={items}
				pagination={{
					current: filter.page,
					pageSize: filter.limit,
					total,
					onChange: handlePageChange,
					showSizeChanger: true,
				}}
				scroll={{ x: 1100 }}
			/>

			<Drawer
				title="Chi tiết liên hệ"
				open={open}
				onClose={handleCloseDrawer}
				width={560}
				destroyOnClose
				footer={
					<Flex justify="flex-end" gap={8}>
						<Button onClick={handleCloseDrawer}>Đóng</Button>
						<Button disabled={!dirty} onClick={handleResetDraft}>
							Hoàn tác
						</Button>
						<Button type="primary" disabled={!dirty} onClick={handleSaveDraft}>
							Lưu
						</Button>
					</Flex>
				}
			>
				{detail ? (
					<Space direction="vertical" size={10} style={{ width: '100%' }}>
						<div>
							<Text type="secondary">Họ tên</Text>
							<div style={{ fontWeight: 600 }}>{detail.fullName}</div>
						</div>

						<div>
							<Text type="secondary">Email</Text>
							<div style={{ fontWeight: 600 }}>{detail.email}</div>
						</div>

						<div>
							<Text type="secondary">Chủ đề</Text>
							<div style={{ fontWeight: 600 }}>{detail.subject}</div>
						</div>

						<div>
							<Text type="secondary">Nội dung</Text>
							<div style={{ whiteSpace: 'pre-wrap' }}>{detail.message}</div>
						</div>

						<div>
							<Text type="secondary">Trạng thái</Text>
							<div style={{ marginTop: 6 }}>
								<Select
									style={{ width: '100%' }}
									value={draft.status}
									options={[
										{ label: 'Mới', value: 'new' },
										{ label: 'Đã đọc', value: 'read' },
										{ label: 'Đã phản hồi', value: 'replied' },
										{ label: 'Đã đóng', value: 'closed' },
									]}
									onChange={(v) => {
										setDraft((prev) => ({ ...prev, status: v }));
										setDirty(true);
									}}
								/>
							</div>
						</div>

						<div>
							<Text type="secondary">Ghi chú nội bộ</Text>
							<Input.TextArea
								rows={4}
								value={draft.note || ''}
								onChange={(e) => {
									setDraft((prev) => ({ ...prev, note: e.target.value }));
									setDirty(true);
								}}
							/>
						</div>
					</Space>
				) : null}
			</Drawer>
		</>
	);
};

export default SH_contact;
